import { z } from 'zod';
import { MemberDTO } from './members.js';

// SAVED = "für später merken": weder angenommen noch abgelehnt.
export const MatchStatus = z.enum(['SUGGESTED', 'CONNECTED', 'REJECTED', 'SAVED']);
export type MatchStatus = z.infer<typeof MatchStatus>;

export const MatchDTO = z.object({
  id: z.string(),
  memberA: MemberDTO,
  memberB: MemberDTO,
  score: z.number(),
  reasons: z.array(z.string()),
  status: MatchStatus,
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MatchDTO = z.infer<typeof MatchDTO>;

// Erkannte Übereinstimmung aus der Heuristik. Bewusst KEINE Punktzahl:
// der angezeigte Score kann von der KI stammen (0–100), die Heuristik-Punkte
// nicht — eine Rechnung daraus wäre falsch. Deshalb nur Signal + Gewicht.
export const MatchFactorDTO = z.object({
  key: z.enum(['tags', 'seeking', 'offering', 'industry', 'city']),
  label: z.string(),
  detail: z.string().nullable(),
  weight: z.enum(['strong', 'medium', 'light']),
});
export type MatchFactorDTO = z.infer<typeof MatchFactorDTO>;

export const MatchSuggestionDTO = z.object({
  candidate: MemberDTO,
  score: z.number(),
  reasons: z.array(z.string()),
  /** Immer heuristisch — bleibt auch erhalten, wenn die KI `reasons` überschreibt. */
  factors: z.array(MatchFactorDTO).default([]),
  aiPowered: z.boolean().optional(),
});
export type MatchSuggestionDTO = z.infer<typeof MatchSuggestionDTO>;

export const MatchExplainInput = z.object({
  memberAId: z.string().min(1),
  memberBId: z.string().min(1),
});
export type MatchExplainInput = z.infer<typeof MatchExplainInput>;

export const MatchExplanationDTO = z.object({
  /** Warum die beiden zusammenpassen. */
  fit: z.string(),
  /** Konkreter Mehrwert der Verbindung. */
  value: z.string(),
  /** Denkbare gemeinsame Projekte. */
  projects: z.array(z.string()),
  /** Business-Potenzial. */
  potential: z.string(),
  /** Konkreter Gesprächsaufhänger ("was sage ich?"). */
  opener: z.string().nullable(),
  /** Erkannte Gemeinsamkeiten aus der Heuristik — auch ohne KI vorhanden. */
  signals: z.array(z.string()),
  aiPowered: z.boolean(),
  /** Gesetzt, wenn die KI nicht verfügbar war — UI zeigt dann nur die Signale. */
  degradedReason: z.string().nullable(),
});
export type MatchExplanationDTO = z.infer<typeof MatchExplanationDTO>;

export const MatchExplainResponse = z.object({
  explanation: MatchExplanationDTO,
});
export type MatchExplainResponse = z.infer<typeof MatchExplainResponse>;

export const MatchCreateInput = z.object({
  memberAId: z.string().min(1),
  memberBId: z.string().min(1),
  score: z.number().int().min(0),
  reasons: z.array(z.string()).default([]),
  status: MatchStatus.optional(),
  notes: z.string().trim().max(2000).optional().nullable(),
});
export type MatchCreateInput = z.infer<typeof MatchCreateInput>;

export const MatchUpdateInput = z.object({
  status: MatchStatus.optional(),
  notes: z.string().trim().max(2000).optional().nullable(),
});
export type MatchUpdateInput = z.infer<typeof MatchUpdateInput>;

export const MatchListQuery = z.object({
  status: MatchStatus.optional(),
  memberId: z.string().optional(),
});
export type MatchListQuery = z.infer<typeof MatchListQuery>;

export const MatchListResponse = z.object({
  matches: z.array(MatchDTO),
});
export type MatchListResponse = z.infer<typeof MatchListResponse>;

export const MatchResponse = z.object({ match: MatchDTO });
export type MatchResponse = z.infer<typeof MatchResponse>;

export const MatchSuggestionsResponse = z.object({
  target: MemberDTO,
  suggestions: z.array(MatchSuggestionDTO),
});
export type MatchSuggestionsResponse = z.infer<typeof MatchSuggestionsResponse>;
